/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useCallback, useRef } from "react";
import Papa from "papaparse";
import * as XLSX from "xlsx";
import { Chart as ChartJS, registerables } from "chart.js";
import { Bar, Pie, Line } from "react-chartjs-2";
import { format, subDays, parseISO, isWithinInterval } from "date-fns";
import { AuthProvider, useAuth } from "react-oidc-context";

import "./App.css";
import "./chartjs-fix";
import "./botonconfig";

// Componentes modulares que ya existen en tu proyecto
import ExportExcelWidget from "./ExportExcelWidget";
import CampaignToggle from "./CampaignToggle";

ChartJS.register(...registerables);

// ========================= CONSTANTES Y CONFIGURACIÓN =========================
const API_CONFIG = {
  BASE: "https://kye5kei8fc.execute-api.us-east-1.amazonaws.com/prod/file-upload",
  CONFIG: "https://m3pmoj8jth.execute-api.us-east-1.amazonaws.com/prod",
  LAMBDA_REFRESH:
    "https://hduvcbpbnrwo7joz7geul537h40omfim.lambda-url.us-east-1.on.aws/",
};

const DEFAULT_CONFIG = {
  llamadas_simultaneas: "2",
  llamadas_simultaneas_bot: "50",
  eliminardias: "60",
  campaign_enable: false,
};

// ========================= AUTH (COGNITO OIDC) =========================
// ⚠️ Asegúrate de que tu Cognito App Client tenga estos URLs en Allowed callback/signout
// - http://localhost:3002/
// - https://afpcreceroutboundcampaign.link/

const COGNITO_REGION = "us-east-1";
const COGNITO_USER_POOL_ID = "us-east-1_0whRAUCjq";
const COGNITO_CLIENT_ID = "3djs9crlte7prdt5sd57nt6cah";

// Dominio Hosted UI (según tu screenshot)
const COGNITO_DOMAIN =
  "https://us-east-10whraucjq.auth.us-east-1.amazoncognito.com";

// Se ajusta solo al dominio/puerto donde corra tu app (localhost o prod)
const APP_ORIGIN =
  typeof window !== "undefined" ? window.location.origin : "http://localhost:3002";
const REDIRECT_URI = `${APP_ORIGIN}/`;
const LOGOUT_URI = `${APP_ORIGIN}/`;

const OIDC_CONFIG = {
  authority: `https://cognito-idp.${COGNITO_REGION}.amazonaws.com/${COGNITO_USER_POOL_ID}`,
  client_id: COGNITO_CLIENT_ID,
  redirect_uri: REDIRECT_URI,
  response_type: "code",
  scope: "openid email phone",
  onSigninCallback: () => {
    // Limpia ?code=...&state=...
    window.history.replaceState({}, document.title, window.location.pathname);
  },
};

// Logout Cognito (Cognito no siempre soporta post_logout_redirect_uri estándar)
function cognitoLogout() {
  const url =
    `${COGNITO_DOMAIN}/logout` +
    `?client_id=${encodeURIComponent(COGNITO_CLIENT_ID)}` +
    `&logout_uri=${encodeURIComponent(LOGOUT_URI)}`;
  window.location.assign(url);
}

function AuthGate({ children }) {
  const auth = useAuth();

  if (auth.isLoading) {
    return (
      <div style={{ padding: 24 }}>
        <h2>Cargando sesión...</h2>
        <p className="muted">Procesando autenticación con Cognito</p>
      </div>
    );
  }

  if (auth.error) {
    return (
      <div style={{ padding: 24 }}>
        <h2>Error de autenticación</h2>
        <pre style={{ whiteSpace: "pre-wrap" }}>
          {String(auth.error?.message || auth.error)}
        </pre>
        <button
          className="btn btn-primary"
          type="button"
          onClick={() => auth.signinRedirect()}
        >
          Reintentar Login
        </button>
      </div>
    );
  }

  if (!auth.isAuthenticated) {
    return (
      <div style={{ padding: 24 }}>
        <h2>Iniciar sesión</h2>
        <p className="muted">
          Necesitas autenticarte para usar el Outbound Campaign Manager.
        </p>
        <button
          className="btn btn-primary"
          type="button"
          onClick={() => auth.signinRedirect()}
        >
          Login con Cognito
        </button>
      </div>
    );
  }

  return <>{children}</>;
}

// ========================= FIX: ID ÚNICO POR CAMPAÑA =========================
const makeCampaignId = (nombreCampania) => {
  const safeName = String(nombreCampania || "campania")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "-")
    .replace(/[^a-z0-9-]/g, "")
    .slice(0, 40);

  const ts = (() => {
    try {
      return format(new Date(), "yyyyMMdd_HHmmss");
    } catch {
      return String(Date.now());
    }
  })();

  const rand = (() => {
    try {
      return (crypto?.randomUUID?.() || Math.random().toString(16).slice(2)).replace(
        /-/g,
        ""
      );
    } catch {
      return Math.random().toString(16).slice(2);
    }
  })();

  return `${safeName}_${ts}_${rand}`;
};

// ✅ SOLO TEMA CLARO (dark mode eliminado)
const THEME_STYLES = `
:root {
  --bg: #ffffff;
  --text: #1e293b;
  --muted: #64748b;
  --card: #f8fafc;
  --border: #e2e8f0;
  --accent: #2563eb;
  --success: #059669;
  --warn: #d97706;
  --danger: #dc2626;
  --chip: rgba(0,0,0,0.06);
}

.app-container { background: var(--bg); color: var(--text); }

.header, .sidebar, .card, .campaign-card, .campaign-control-card, .chart-card,
.config-table table, .preview-table table {
  background: var(--card);
  border-color: var(--border) !important;
}

.header, .card, .campaign-card, .chart-card, .config-table table, .preview-table table {
  border: 1px solid var(--border);
}

.header .user-menu span, .muted, .table-info, .preview-info, .filter-label span, .metric-label {
  color: var(--muted);
}

.btn, .btn-primary, .btn-outline, .btn-success, .btn-danger, .btn-secondary, .btn-refresh, .btn-delete-campaign {
  color: var(--text);
}

.btn-primary, .btn-refresh, .btn-success {
  background: var(--accent);
  color: white;
  border-color: var(--accent);
}

.btn-delete-campaign, .btn-danger {
  background: var(--danger);
  color: white;
  border-color: var(--danger);
}

.input, .campaign-select, .search-box input, .date-input, input[type="number"], input[type="text"], select {
  background: var(--card);
  color: var(--text);
  border: 1px solid var(--border);
}

.table-responsive table th, .table-responsive table td { border-color: var(--border); }

.badge-upload {
  background: var(--chip);
  color: var(--text);
  border: 1px dashed var(--border);
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 12px;
}

.status-badge {
  background: var(--chip);
  color: var(--text);
  border: 1px solid var(--border);
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 12px;
}
`;

// ========================= SERVICIOS Y UTILIDADES =========================
class ApiService {
  static async fetchWithRetry(url, options = {}, retries = 3) {
    try {
      const response = await fetch(url, options);

      const ct = response.headers.get("content-type") || "";
      const isJson = ct.includes("application/json");

      if (!response.ok) {
        const msg = await (isJson
          ? response.json().catch(() => null)
          : response.text().catch(() => ""));
        throw new Error(
          `HTTP error! status: ${response.status} ${
            typeof msg === "string" ? msg : ""
          }`
        );
      }

      const data = await (isJson ? response.json() : response.text());

      if (typeof data === "string") {
        try {
          return JSON.parse(data);
        } catch {
          return { raw: data };
        }
      }

      return data;
    } catch (error) {
      if (retries > 0) {
        await new Promise((resolve) => setTimeout(resolve, 1000));
        return this.fetchWithRetry(url, options, retries - 1);
      }
      throw error;
    }
  }

  static async triggerBackendRefresh() {
    try {
      await fetch(API_CONFIG.LAMBDA_REFRESH, { method: "POST" });
    } catch (e) {
      console.warn("No se pudo refrescar el backend (Lambda):", e);
    }
  }

  static async fetchGlobalConfig() {
    try {
      const response = await fetch(`${API_CONFIG.BASE}?config=1`);
      if (!response.ok) throw new Error("Error al obtener configuración");
      const data = await response.json();

      return {
        llamadas_simultaneas: String(data.llamadas_simultaneas || 2),
        llamadas_simultaneas_bot: String(data.llamadas_simultaneas_bot || 50),
        eliminardias: String(data.eliminardias || 60),
        campaign_enable: String(data.campaign_enable || "false") === "true",
      };
    } catch (err) {
      console.error("Error fetching global config:", err);
      return DEFAULT_CONFIG;
    }
  }

  static async deleteCampaignByName(nombreCampania, { dryRun = false } = {}) {
    if (!nombreCampania) throw new Error("nombre_campania requerido");

    const res = await fetch(`${API_CONFIG.BASE}?delete_campaign=1`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ nombre_campania: nombreCampania, dry_run: !!dryRun }),
    });

    if (!res.ok) {
      const t = await res.text().catch(() => "");
      throw new Error(`Error eliminando campaña: HTTP ${res.status} ${t}`);
    }

    return res.json().catch(async () => ({ raw: await res.text().catch(() => "") }));
  }
}

class DataUtils {
  static formatCedula(cedula) {
    return cedula == null ? "" : String(cedula);
  }

  static normalizeStr(s) {
    return String(s ?? "")
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/\s+/g, " ")
      .trim()
      .toLowerCase();
  }

  static tryParseDate(v) {
    if (!v) return null;
    try {
      if (v instanceof Date) return isNaN(v) ? null : v;

      if (typeof v === "number") {
        const d = new Date(v);
        return isNaN(d) ? null : d;
      }

      if (typeof v === "string" && /^\d+$/.test(v)) {
        const d = new Date(Number(v));
        return isNaN(d) ? null : d;
      }

      if (typeof v === "string") {
        const d = parseISO(v);
        if (!isNaN(d)) return d;
      }

      const d2 = new Date(v);
      return isNaN(d2) ? null : d2;
    } catch {
      return null;
    }
  }

  static extractUploadDate(item) {
    const candidates = [
      item?.campaign_uploaded_at,
      item?.fecha_subida,
      item?.upload_time,
      item?.fecha_carga,
      item?.created_at,
      item?.createdAt,
      item?.created,
      item?.timestamp,
      item?.ultimo_intento,
    ];
    for (const c of candidates) {
      const d = this.tryParseDate(c);
      if (d) return d;
    }
    return null;
  }

  static extractAfpAppointmentDate(item) {
    const candidates = [item?.fecha_cita_afp, item?.cita_afp];
    for (const c of candidates) {
      const d = this.tryParseDate(c);
      if (d) return d;
    }
    return null;
  }

  static formatUpload(dt) {
    if (!dt) return "desconocido";
    try {
      return format(dt, "dd/MM/yyyy HH:mm");
    } catch {
      return "desconocido";
    }
  }

  static formatDateTime12h(dt) {
    if (!dt) return "-";
    try {
      return format(dt, "dd/MM/yyyy hh:mm a").toLowerCase();
    } catch {
      return "-";
    }
  }
}

// ========================= FIX CLAVE: NO REEMPLAZAR CAMPAÑAS (MERGE + CACHE) =========================
const STORAGE_KEYS = {
  RAW_DATA: "ocm_rawData_cache_v1",
};

const getRecordKey = (r) => {
  const cid = String(r?.campaign_id || r?.campaignId || "").trim();
  const name = String(r?.nombre_campania || "").trim();
  const phone = String(r?.phoneNumber || "").trim();

  if (cid && phone) return `cid:${cid}#ph:${phone}`;
  if (name && phone) return `name:${DataUtils.normalizeStr(name)}#ph:${phone}`;

  const ced = String(r?.cedula || "").trim();
  const nom = String(r?.nombres || "").trim();
  return `fallback:${DataUtils.normalizeStr(name)}#${phone || ced || nom}`;
};

const pickNewerRecord = (a, b) => {
  const da =
    DataUtils.tryParseDate(a?.ultimo_intento) ||
    DataUtils.tryParseDate(a?.updated_at) ||
    DataUtils.tryParseDate(a?.updatedAt) ||
    DataUtils.extractUploadDate(a);

  const db =
    DataUtils.tryParseDate(b?.ultimo_intento) ||
    DataUtils.tryParseDate(b?.updated_at) ||
    DataUtils.tryParseDate(b?.updatedAt) ||
    DataUtils.extractUploadDate(b);

  if (da && db) return db > da ? { ...a, ...b } : { ...b, ...a };
  if (db && !da) return { ...a, ...b };
  if (da && !db) return { ...b, ...a };
  return { ...a, ...b };
};

const mergeRecords = (prevArr, nextArr) => {
  const map = new Map();
  const all = [
    ...(Array.isArray(prevArr) ? prevArr : []),
    ...(Array.isArray(nextArr) ? nextArr : []),
  ];

  for (const r of all) {
    const k = getRecordKey(r);
    const existing = map.get(k);
    map.set(k, existing ? pickNewerRecord(existing, r) : r);
  }

  return Array.from(map.values());
};

// ========================= COMPONENTES DE UI =========================
const Header = ({ sidebarCollapsed, setSidebarCollapsed }) => {
  const auth = useAuth();

  const userLabel = (() => {
    const prof = auth?.user?.profile;
    return (
      prof?.email ||
      prof?.preferred_username ||
      prof?.username ||
      "Admin"
    );
  })();

  return (
    <header className="header">
      <div className="header-left">
        <button
          className="sidebar-toggle"
          onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
          title={sidebarCollapsed ? "Expandir menú" : "Colapsar menú"}
          type="button"
        >
          <i className={`fas fa-${sidebarCollapsed ? "indent" : "dedent"}`}></i>
        </button>
        <div className="logo">
          <div className="logo-text">
            <span className="logo-highlight">Outbound</span> Campaign Manager
          </div>
        </div>
      </div>

      <div className="header-right">
        <div className="user-menu" style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <i className="fas fa-user-circle"></i>
          <span title={userLabel}>{userLabel}</span>
          <button
            className="btn btn-outline"
            type="button"
            onClick={() => cognitoLogout()}
            title="Cerrar sesión"
          >
            <i className="fas fa-sign-out-alt"></i> Salir
          </button>
        </div>
      </div>
    </header>
  );
};

const Sidebar = ({ page, setPage }) => (
  <aside className="sidebar">
    <div className="sidebar-header">
      <h3>Menú Principal</h3>
    </div>
    <ul className="sidebar-menu">
      <li className={page === "metrics" ? "active" : ""}>
        <a
          href="#"
          onClick={(e) => {
            e.preventDefault();
            setPage("metrics");
          }}
        >
          <i className="fas fa-tachometer-alt"></i>
          <span>Dashboard</span>
        </a>
      </li>
      <li className={page === "config" ? "active" : ""}>
        <a
          href="#"
          onClick={(e) => {
            e.preventDefault();
            setPage("config");
          }}
        >
          <i className="fas fa-plus-circle"></i>
          <span>Nueva Campaña</span>
        </a>
      </li>
    </ul>
    <div className="sidebar-footer">
      <div className="system-status">
        <div className="status-indicator active"></div>
        <span>Sistema Operativo</span>
      </div>
    </div>
  </aside>
);

// ========================= HOOKS PERSONALIZADOS =========================
const useCampaignData = () => {
  const initialCache = (() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEYS.RAW_DATA);
      const parsed = raw ? JSON.parse(raw) : null;
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  })();

  const [rawData, setRawData] = useState(initialCache);
  const [loadingData, setLoadingData] = useState(initialCache.length === 0);
  const [dataError, setDataError] = useState(null);

  const setRawDataPersist = useCallback((updater) => {
    setRawData((prev) => {
      const next = typeof updater === "function" ? updater(prev) : updater;
      try {
        localStorage.setItem(STORAGE_KEYS.RAW_DATA, JSON.stringify(next));
      } catch {}
      return next;
    });
  }, []);

  const refreshData = useCallback(async () => {
    try {
      setLoadingData(true);
      setDataError(null);

      const response = await ApiService.fetchWithRetry(`${API_CONFIG.BASE}`);
      const items =
        response?.items || response?.Items || response?.data?.items || null;

      if (Array.isArray(items)) {
        const datosFormateados = items.map((item) => {
          const afpDt = DataUtils.extractAfpAppointmentDate(item);
          return {
            ...item,
            cedula: DataUtils.formatCedula(item.cedula),
            cita_afp: afpDt ? afpDt.toISOString() : item?.fecha_cita_afp || item?.cita_afp || "",
          };
        });

        setRawDataPersist((prev) => mergeRecords(prev, datosFormateados));
      } else {
        setDataError("No se pudieron cargar los datos (respuesta sin items)");
      }
    } catch (err) {
      console.error("Error al cargar datos:", err);
      setDataError("Error al cargar datos de DynamoDB");
    } finally {
      setLoadingData(false);
    }
  }, [setRawDataPersist]);

  return {
    rawData,
    loadingData,
    dataError,
    refreshData,
    setRawData: setRawDataPersist,
  };
};

const useAutoRefresh = (refreshData, autoRefresh, refreshMs) => {
  const refreshGuard = useRef(false);

  useEffect(() => {
    if (!autoRefresh) return;

    const tick = async () => {
      if (document.hidden) return;
      if (refreshGuard.current) return;

      refreshGuard.current = true;
      try {
        await refreshData();
      } finally {
        refreshGuard.current = false;
      }
    };

    tick();
    const id = setInterval(tick, refreshMs);
    return () => clearInterval(id);
  }, [autoRefresh, refreshMs, refreshData]);
};

// ✅ Tooltip negro con texto blanco (para todas)
const WHITE_ON_BLACK_TOOLTIP = {
  backgroundColor: "rgba(0,0,0,0.85)",
  borderColor: "rgba(255,255,255,0.25)",
  borderWidth: 1,
  titleColor: "#ffffff",
  bodyColor: "#ffffff",
  footerColor: "#ffffff",
};

// ========================= APP ROOT (AuthProvider + Gate) =========================
export default function AppRoot() {
  return (
    <AuthProvider {...OIDC_CONFIG}>
      <AuthGate>
        <AppShell />
      </AuthGate>
    </AuthProvider>
  );
}

// ========================= COMPONENTE PRINCIPAL =========================
function AppShell() {
  const [page, setPage] = useState("metrics");
  const [datosCampania, setDatosCampania] = useState(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [filtrocamp, setFiltrocamp] = useState("");
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [refreshMs, setRefreshMs] = useState(30000);

  const { rawData, loadingData, dataError, refreshData, setRawData } =
    useCampaignData();

  useAutoRefresh(refreshData, autoRefresh, refreshMs);

  // ✅ Defaults ChartJS (tema claro fijo)
  useEffect(() => {
    ChartJS.defaults.color = "#202124";
    ChartJS.defaults.borderColor = "rgba(0,0,0,0.08)";
  }, []);

  useEffect(() => {
    refreshData();
  }, [refreshData]);

  const renderContent = () => {
    switch (page) {
      case "metrics":
        return (
          <>
            <MetricsDashboard
              rawData={rawData}
              loading={loadingData}
              error={dataError}
              onRefresh={refreshData}
              autoRefresh={autoRefresh}
              setAutoRefresh={setAutoRefresh}
              refreshMs={refreshMs}
              setRefreshMs={setRefreshMs}
              filtrocamp={filtrocamp}
              setFiltrocamp={setFiltrocamp}
            />
            <div className="data-section">
              <h2>Registros de Campañas</h2>
              <DynamoDBTableView
                data={rawData}
                setRawData={setRawData}
                loading={loadingData}
                error={dataError}
                onRefresh={refreshData}
                autoRefresh={autoRefresh}
                refreshMs={refreshMs}
                filtrocamp={filtrocamp}
                setFiltrocamp={setFiltrocamp}
              />
            </div>
          </>
        );
      case "campania":
        return (
          <CampaniaPage
            datos={datosCampania}
            goToConfig={() => setPage("config")}
            goToDashboard={() => {
              setFiltrocamp("");
              setPage("metrics");
            }}
            refreshDashboardData={refreshData}
          />
        );
      case "config":
        return (
          <ConfigPage
            goToCampania={(datos) => {
              setDatosCampania(datos);
              setPage("campania");
            }}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className={`app-container ${sidebarCollapsed ? "sidebar-collapsed" : ""}`}>
      <style>{THEME_STYLES}</style>

      <Header
        sidebarCollapsed={sidebarCollapsed}
        setSidebarCollapsed={setSidebarCollapsed}
      />

      <div className="main-container">
        <Sidebar page={page} setPage={setPage} sidebarCollapsed={sidebarCollapsed} />
        <main className="content-area">{renderContent()}</main>
      </div>
    </div>
  );
}

// ========================= COMPONENTE DASHBOARD =========================
function MetricsDashboard({
  rawData,
  loading,
  error,
  onRefresh,
  autoRefresh,
  setAutoRefresh,
  refreshMs,
  setRefreshMs,
  filtrocamp,
  setFiltrocamp,
}) {
  const [dateRange, setDateRange] = useState({
    start: subDays(new Date(), 7),
    end: new Date(),
  });
  const [flowType, setFlowType] = useState("todos");
  const [metricsData, setMetricsData] = useState({
    estados: [],
    campaigns: [],
    dailyStats: [],
  });
  const [callMetricsData, setCallMetrics] = useState({
    llamadas_realizadas: 0,
    llamadas_completadas: 0,
    efectividad: 0,
    duracion_promedio_segundos: 0,
    registros: [],
  });

  const campaignOptions = React.useMemo(() => {
    const map = new Map();
    (rawData || []).forEach((i) => {
      const originalName = (i?.nombre_campania || "Sin nombre").trim();
      const campaignId = (i?.campaign_id || i?.campaignId || "").trim();
      const display = campaignId
        ? `${originalName} (${campaignId.slice(0, 8)})`
        : originalName;
      const norm = DataUtils.normalizeStr(display);
      if (display && !map.has(norm)) map.set(norm, display);
    });
    return Array.from(map.values()).sort((a, b) => a.localeCompare(b));
  }, [rawData]);

  const resolveFilterToMatch = (itemNombre, itemCampaignId) => {
    const n = (itemNombre || "Sin nombre").trim();
    const cid = (itemCampaignId || "").trim();
    return cid ? `${n} (${cid.slice(0, 8)})` : n;
  };

  const filterForStates = (data) =>
    (data || []).filter((item) => {
      if (
        flowType !== "todos" &&
        String(item.tipoflujo || "").toLowerCase() !== flowType.toLowerCase()
      )
        return false;

      if (filtrocamp) {
        const disp = resolveFilterToMatch(
          item.nombre_campania,
          item.campaign_id || item.campaignId
        );
        if (DataUtils.normalizeStr(disp) !== DataUtils.normalizeStr(filtrocamp))
          return false;
      }
      return true;
    });

  const filterForCharts = (data) =>
    (data || []).filter((item) => {
      const d = DataUtils.tryParseDate(item.ultimo_intento);
      if (!d) return false;

      if (!isWithinInterval(d, { start: dateRange.start, end: dateRange.end }))
        return false;

      if (
        flowType !== "todos" &&
        String(item.tipoflujo || "").toLowerCase() !== flowType.toLowerCase()
      )
        return false;

      if (filtrocamp) {
        const disp = resolveFilterToMatch(
          item.nombre_campania,
          item.campaign_id || item.campaignId
        );
        if (DataUtils.normalizeStr(disp) !== DataUtils.normalizeStr(filtrocamp))
          return false;
      }
      return true;
    });

  const filterForCampaigns = (data) =>
    (data || []).filter((item) => {
      if (
        flowType !== "todos" &&
        String(item.tipoflujo || "").toLowerCase() !== flowType.toLowerCase()
      )
        return false;

      if (filtrocamp) {
        const disp = resolveFilterToMatch(
          item.nombre_campania,
          item.campaign_id || item.campaignId
        );
        if (DataUtils.normalizeStr(disp) !== DataUtils.normalizeStr(filtrocamp))
          return false;
      }
      return true;
    });

  const processData = useCallback((itemsForStates, itemsForCharts, itemsForCampaigns) => {
    const estadosCountMap = {};
    const LABEL_MAP = {
      pendiente: "Pendientes",
      pendientes: "Pendientes",
      completada: "Completadas",
      completado: "Completadas",
      completadas: "Completadas",
      fallida: "Fallidas",
      fallido: "Fallidas",
      fallidas: "Fallidas",
      fallidos: "Fallidas",
    };

    itemsForStates.forEach((item) => {
      const raw = String(item.estado ?? "").trim();
      const normalized = raw.toLowerCase();
      const labelBase = LABEL_MAP[normalized] || raw || "Sin estado";

      estadosCountMap[labelBase] = (estadosCountMap[labelBase] || 0) + 1;

      if (item.fecha_reagenda) {
        estadosCountMap["Agendadas"] = (estadosCountMap["Agendadas"] || 0) + 1;
      }
    });

    const estadosArray = Object.entries(estadosCountMap)
      .map(([estado, cantidad]) => ({ estado, cantidad }))
      .sort((a, b) => b.cantidad - a.cantidad);

    const dailyStatsMap = {};
    itemsForCharts.forEach((item) => {
      const d = DataUtils.tryParseDate(item.ultimo_intento);
      if (!d) return;

      const dateKey = format(d, "yyyy-MM-dd");
      if (!dailyStatsMap[dateKey])
        dailyStatsMap[dateKey] = { date: dateKey, llamadas: 0, completadas: 0 };

      dailyStatsMap[dateKey].llamadas++;
      const e = String(item.estado || "").toLowerCase();
      if (e.startsWith("completad")) dailyStatsMap[dateKey].completadas++;
    });

    const campaignsMap = {};
    itemsForCampaigns.forEach((item) => {
      const campania = (item.nombre_campania || "Sin nombre").trim();
      const campaignId = (item.campaign_id || item.campaignId || "").trim();
      const display = campaignId
        ? `${campania} (${campaignId.slice(0, 8)})`
        : campania;
      const key = DataUtils.normalizeStr(display);

      if (!campaignsMap[key]) {
        campaignsMap[key] = { nombre: display, cantidad: 0, uploadedAt: null };
      }
      campaignsMap[key].cantidad++;

      const cand = DataUtils.extractUploadDate(item);
      if (cand) {
        const prev = campaignsMap[key].uploadedAt;
        campaignsMap[key].uploadedAt = prev ? (cand < prev ? cand : prev) : cand;
      }
    });

    setMetricsData({
      estados: estadosArray,
      campaigns: Object.values(campaignsMap),
      dailyStats: Object.values(dailyStatsMap).sort(
        (a, b) => new Date(a.date) - new Date(b.date)
      ),
    });

    const itemsForKPIs = itemsForStates;

    let totalPendientes = 0;
    let totalFallidas = 0;

    itemsForKPIs.forEach((item) => {
      const e = String(item.estado || "").trim().toLowerCase();
      if (e === "pendiente" || e === "pendientes") totalPendientes++;
      else if (
        e === "fallida" ||
        e === "fallidas" ||
        e === "fallido" ||
        e === "fallidos"
      )
        totalFallidas++;
    });

    const registrosEfectivos = itemsForKPIs.length - totalPendientes - totalFallidas;
    const totalRegistros = itemsForKPIs.length;
    const efectividad =
      totalRegistros > 0 ? (registrosEfectivos / totalRegistros) * 100 : 0;

    const llamadasRealizadas = itemsForKPIs.reduce(
      (total, item) => total + (Number(item.intentos_actuales) || 0),
      0
    );

    setCallMetrics({
      llamadas_realizadas: llamadasRealizadas,
      llamadas_completadas: registrosEfectivos,
      efectividad,
      duracion_promedio_segundos: 0,
      registros: itemsForKPIs.map((item) => {
        const e = String(item.estado || "").toLowerCase();
        return {
          call_id: item.phoneNumber,
          campaign_name: (item.nombre_campania || "Sin nombre").trim(),
          phone_number: item.phoneNumber,
          flow_type: item.tipoflujo || "",
          status: (() => {
            if (e === "completada" || e === "completado") return "completed";
            if (
              e === "fallida" ||
              e === "fallido" ||
              e === "fallidas" ||
              e === "fallidos"
            )
              return "failed";
            if (e === "pendiente" || e === "pendientes") return "pending";
            return e || "unknown";
          })(),
          duration: 0,
          start_time: item.ultimo_intento || item.created_at || new Date().toISOString(),
          end_time: item.ultimo_intento || item.created_at || new Date().toISOString(),
        };
      }),
    });
  }, []);

  useEffect(() => {
    const itemsForStates = filterForStates(rawData);
    const itemsForCharts = filterForCharts(rawData);
    const itemsForCampaigns = filterForCampaigns(rawData);
    processData(itemsForStates, itemsForCharts, itemsForCampaigns);
  }, [rawData, dateRange, flowType, filtrocamp, processData]);

  const textColor = "#202124";
  const gridColor = "rgba(0,0,0,0.06)";

  const estadosLabels = metricsData.estados.map(({ estado }) => {
    const label = String(estado || "").replace(/_/g, " ");
    return label.charAt(0).toUpperCase() + label.slice(1).toLowerCase();
  });

  const estadosCounts = metricsData.estados.map((e) => e.cantidad);
  const totalEstados = estadosCounts.reduce((a, b) => a + b, 0);

  const baseBgColors = [
    "rgba(255, 165, 0, 0.7)",
    "rgba(33, 150, 243, 0.7)",
    "rgba(244, 67, 54, 0.7)",
    "rgba(76, 175, 80, 0.7)",
    "rgba(156, 163, 175, 0.7)",
    "rgba(129, 140, 248, 0.7)",
  ];

  const baseBorderColors = [
    "rgba(255, 165, 0, 1)",
    "rgba(33, 150, 243, 1)",
    "rgba(244, 67, 54, 1)",
    "rgba(76, 175, 80, 1)",
    "rgba(156, 163, 175, 1)",
    "rgba(129, 140, 248, 1)",
  ];

  const dynamicBgColors = estadosLabels.map((_, idx) => baseBgColors[idx % baseBgColors.length]);
  const dynamicBorderColors = estadosLabels.map(
    (_, idx) => baseBorderColors[idx % baseBorderColors.length]
  );

  const estadosLlamadasData =
    totalEstados > 0
      ? {
          labels: estadosLabels,
          datasets: [
            {
              data: estadosCounts,
              backgroundColor: dynamicBgColors,
              borderColor: dynamicBorderColors,
              borderWidth: 1,
              hoverOffset: 10,
            },
          ],
        }
      : {
          labels: ["Sin datos"],
          datasets: [
            {
              data: [1],
              backgroundColor: ["rgba(150,150,150,0.4)"],
              borderColor: ["rgba(150,150,150,0.7)"],
              borderWidth: 1,
              hoverOffset: 10,
            },
          ],
        };

  const campaignsLabels = metricsData.campaigns.map((c) => c.nombre);
  const campaignsValues = metricsData.campaigns.map((c) => c.cantidad);
  const campaignsUploadedAt = metricsData.campaigns.map((c) => c.uploadedAt);

  const campañasActivasData = {
    labels: campaignsLabels,
    datasets: [
      {
        label: "Contactos",
        data: campaignsValues,
        backgroundColor: campaignsLabels.map(
          (_, index) => `hsl(${(index * 57) % 360}, 70%, 70%)`
        ),
        borderColor: campaignsLabels.map(
          (_, index) => `hsl(${(index * 57) % 360}, 70%, 45%)`
        ),
        borderWidth: 1,
        borderRadius: 4,
      },
    ],
  };

  const dailyTrendData = {
    labels: metricsData.dailyStats.map((d) => format(new Date(d.date), "MMM dd")),
    datasets: [
      {
        label: "Llamadas Realizadas",
        data: metricsData.dailyStats.map((d) => d.llamadas),
        borderColor: "rgba(33, 150, 243, 1)",
        backgroundColor: "rgba(33, 150, 243, 0.1)",
        tension: 0.3,
        fill: true,
      },
      {
        label: "Llamadas Completadas",
        data: metricsData.dailyStats.map((d) => d.completadas),
        borderColor: "rgba(76, 175, 80, 1)",
        backgroundColor: "rgba(76, 175, 80, 0.1)",
        tension: 0.3,
        fill: true,
      },
    ],
  };

  const totalContactosPorLlamar = metricsData.estados.reduce((sum, e) => {
    const est = String(e.estado || "");
    return est === "Pendientes" || est === "Agendadas" ? sum + e.cantidad : sum;
  }, 0);

  const filteredUploadBadge = (() => {
    if (!filtrocamp) return null;
    const idx = campaignsLabels.findIndex((n) => n === filtrocamp);
    if (idx === -1) return null;
    const dt = campaignsUploadedAt[idx];
    return dt ? `Subido: ${DataUtils.formatUpload(dt)}` : null;
  })();

  if (loading) return <LoadingSpinner message="Cargando métricas..." />;

  return (
    <div className="dashboard-container">
      <div className="dashboard-header">
        <div className="dashboard-title-container">
          <div className="dashboard-logo">
            <i className="fas fa-phone-volume"></i>
          </div>
          <div>
            <h1>Dashboard de Campañas</h1>
            <p className="muted">Monitoreo y gestión de campañas de outbound</p>
          </div>
        </div>
        <div className="header-actions">
          <button className="btn btn-primary" onClick={() => onRefresh?.()} type="button">
            <i className="fas fa-sync-alt"></i> Actualizar
          </button>
        </div>
      </div>

      <div className="campaign-control-section">
        <div className="campaign-control-card enhanced">
          <div className="control-header">
            <div className="control-icon">
              <i className="fas fa-sliders-h"></i>
            </div>
            <div className="control-info">
              <h3>Control Global de Campañas</h3>
              <p className="muted">
                Activa o desactiva todas las campañas de outbound simultáneamente
              </p>
            </div>
          </div>
          <div className="control-action">
            <CampaignToggle apiEndpoint="https://orp6yaxiago7jck4enpo6jikam0uyvxl.lambda-url.us-east-1.on.aws/campaign_enable" />
          </div>
        </div>
      </div>

      <div className="filters-container enhanced">
        <div className="filter-group enhanced">
          <div className="filter-label">
            <i className="fas fa-calendar"></i>
            <span>Rango de Fechas</span>
          </div>
          <div className="date-range enhanced">
            <input
              type="date"
              value={format(dateRange.start, "yyyy-MM-dd")}
              onChange={(e) =>
                setDateRange({ ...dateRange, start: parseISO(e.target.value) })
              }
              className="date-input"
            />
            <span className="date-separator">hasta</span>
            <input
              type="date"
              value={format(dateRange.end, "yyyy-MM-dd")}
              onChange={(e) =>
                setDateRange({ ...dateRange, end: parseISO(e.target.value) })
              }
              className="date-input"
            />
          </div>
        </div>

        <div className="filter-group enhanced">
          <div className="filter-label">
            <i className="fas fa-filter"></i>
            <span>Filtrar por Campaña</span>
          </div>
          <div className="select-wrapper">
            <select
              value={filtrocamp}
              onChange={(e) => setFiltrocamp(e.target.value)}
              className="campaign-select"
            >
              <option value="">Todas las campañas</option>
              {campaignOptions.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
            <i className="fas fa-chevron-down select-arrow"></i>
          </div>
          {filtrocamp && (
            <button
              className="btn btn-clear-filter"
              onClick={() => setFiltrocamp("")}
              title="Limpiar filtro"
              type="button"
            >
              <i className="fas fa-times"></i>
            </button>
          )}
        </div>

        <div
          className="filter-group enhanced"
          style={{ display: "flex", alignItems: "center", gap: 8 }}
        >
          <select
            value={refreshMs}
            onChange={(e) => setRefreshMs?.(Number(e.target.value))}
            className="campaign-select"
          >
            <option value={15000}>15s</option>
            <option value={30000}>30s</option>
            <option value={60000}>1 min</option>
          </select>
        </div>

        <label className="toggle" style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
          <input
            type="checkbox"
            checked={autoRefresh}
            onChange={(e) => setAutoRefresh?.(e.target.checked)}
          />
          <span>Auto</span>
        </label>
      </div>

      <ExportExcelWidget records={filterForCharts(rawData)} filename="dashboard.xlsx" />

      {error && <ErrorMessage message={error} />}

      {!loading && (
        <>
          <div className="metrics-grid">
            <MetricCard
              value={callMetricsData.llamadas_completadas}
              label="Completadas"
              icon="check-circle"
              type="success"
            />
            <MetricCard
              value={totalContactosPorLlamar}
              label="Por Llamar"
              icon="phone"
              type="warning"
            />
            <MetricCard
              value={campaignsLabels.length}
              label="Campañas (en filtro)"
              icon="bullhorn"
              type="info"
            />
            <MetricCard
              value={callMetricsData.llamadas_realizadas}
              label="Llamadas Realizadas"
              icon="phone-volume"
              type="primary"
            />
            <MetricCard
              value={`${callMetricsData.efectividad?.toFixed?.(2) || 0}%`}
              label="Efectividad"
              icon="chart-line"
              type="secondary"
            />
          </div>

          <div className="charts-grid">
            <ChartCard title="Estados de Llamadas">
              <Pie
                data={estadosLlamadasData}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: {
                    legend: {
                      position: "right",
                      labels: {
                        padding: 20,
                        usePointStyle: true,
                        pointStyle: "circle",
                        color: textColor,
                      },
                    },
                    tooltip: {
                      ...WHITE_ON_BLACK_TOOLTIP,
                      callbacks:
                        totalEstados > 0
                          ? {
                              label: function (context) {
                                return `${context.label}: ${context.raw}`;
                              },
                            }
                          : {
                              label: function () {
                                return `Sin datos`;
                              },
                            },
                    },
                  },
                  cutout: "60%",
                  animation: { animateScale: true, animateRotate: true },
                }}
              />
            </ChartCard>

            <ChartCard
              title={
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  Volumen por Campaña
                  {filteredUploadBadge && (
                    <span className="badge-upload" title="Fecha y hora de subida del Excel">
                      {filteredUploadBadge}
                    </span>
                  )}
                </div>
              }
            >
              <Bar
                data={campañasActivasData}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: {
                    legend: { display: false },
                    tooltip: {
                      ...WHITE_ON_BLACK_TOOLTIP,
                      callbacks: {
                        label: function (ctx) {
                          return `Contactos: ${ctx.raw}`;
                        },
                        afterLabel: function (ctx) {
                          const idx = ctx.dataIndex;
                          const up = campaignsUploadedAt[idx];
                          return `Subido: ${DataUtils.formatUpload(up)}`;
                        },
                      },
                    },
                  },
                  scales: {
                    y: {
                      beginAtZero: true,
                      grid: { drawBorder: false, color: gridColor },
                      ticks: { color: textColor },
                    },
                    x: {
                      grid: { display: false, color: gridColor },
                      ticks: { color: textColor },
                    },
                  },
                  onClick: (evt, elements) => {
                    if (elements?.length) {
                      const idx = elements[0].index;
                      const nombre = campaignsLabels[idx];
                      if (nombre) setFiltrocamp((prev) => (prev === nombre ? "" : nombre));
                    }
                  },
                }}
              />
            </ChartCard>

            <ChartCard title="Tendencia Diaria">
              <Line
                data={dailyTrendData}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: {
                    legend: { position: "top", labels: { color: textColor } },
                    tooltip: { ...WHITE_ON_BLACK_TOOLTIP },
                  },
                  scales: {
                    y: { grid: { color: gridColor }, ticks: { color: textColor } },
                    x: { grid: { color: gridColor }, ticks: { color: textColor } },
                  },
                  interaction: { intersect: false, mode: "index" },
                }}
              />
            </ChartCard>
          </div>
        </>
      )}
    </div>
  );
}

// ========================= COMPONENTES AUXILIARES =========================
const MetricCard = ({ value, label, icon, type = "primary" }) => (
  <div className="metric-card">
    <div className="metric-value">{value}</div>
    <div className="metric-label">{label}</div>
    <div className={`metric-icon ${type}`}>
      <i className={`fas fa-${icon}`}></i>
    </div>
  </div>
);

const ChartCard = ({ title, children, actions }) => (
  <div className="chart-card">
    <div className="chart-header">
      <h3>{title}</h3>
      <div className="chart-actions">
        {actions || (
          <button className="btn-icon" type="button">
            <i className="fas fa-ellipsis-v"></i>
          </button>
        )}
      </div>
    </div>
    <div className="chart-container">{children}</div>
  </div>
);

// ========================= HELPERS TABLAS =========================
const getCampaignDisplay = (item) => {
  const name = (item?.nombre_campania || "Sin nombre").trim();
  const cid = (item?.campaign_id || item?.campaignId || "").trim();
  return cid ? `${name} (${cid.slice(0, 8)})` : name;
};

const matchCampaignFilter = (item, filtrocamp) => {
  if (!filtrocamp) return true;
  const display = getCampaignDisplay(item);
  return DataUtils.normalizeStr(display) === DataUtils.normalizeStr(filtrocamp);
};

// ========================= COMPONENTE TABLA DYNAMODB =========================
function DynamoDBTableView({
  data,
  setRawData,
  loading,
  error,
  onRefresh,
  autoRefresh,
  refreshMs,
  filtrocamp,
  setFiltrocamp,
}) {
  const [selectedCampaign, setSelectedCampaign] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [deleteMsg, setDeleteMsg] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);

  const refreshBtnRef = useRef(null);

  const uniqueCampaigns = React.useMemo(() => {
    const map = new Map();
    (data || []).forEach((it) => {
      const display = getCampaignDisplay(it);
      const norm = DataUtils.normalizeStr(display);
      if (display && !map.has(norm)) map.set(norm, display);
    });
    return Array.from(map.values()).sort((a, b) => a.localeCompare(b));
  }, [data]);

  const askDeleteFromTable = () => {
    if (!selectedCampaign) {
      setDeleteMsg("Selecciona una campaña primero.");
      return;
    }
    setConfirmOpen(true);
  };

  const doDeleteFromTable = async () => {
    setConfirmOpen(false);
    setDeleting(true);
    setDeleteMsg("Eliminando campaña...");
    try {
      const cleanName = selectedCampaign.replace(/\s\([a-f0-9]{8}\)$/i, "").trim();
      const r = await ApiService.deleteCampaignByName(cleanName, { dryRun: false });
      const deleted = typeof r?.deleted === "number" ? r.deleted : 0;
      setDeleteMsg(`✅ Eliminados ${deleted} registros (encontrados: ${r?.found ?? "?"}).`);

      setRawData((prev) =>
        prev.filter(
          (x) =>
            DataUtils.normalizeStr((x.nombre_campania || "Sin nombre").trim()) !==
            DataUtils.normalizeStr(cleanName)
        )
      );

      setSelectedCampaign("");

      if (DataUtils.normalizeStr(filtrocamp) === DataUtils.normalizeStr(selectedCampaign)) {
        setFiltrocamp("");
      }
    } catch (e) {
      console.error(e);
      setDeleteMsg("❌ Error eliminando la campaña.");
    } finally {
      setDeleting(false);
    }
  };

  const campaignScopedData = React.useMemo(() => {
    const src = Array.isArray(data) ? data : [];
    return src.filter((it) => matchCampaignFilter(it, filtrocamp));
  }, [data, filtrocamp]);

  if (loading) {
    return (
      <div className="card">
        <div className="card-header">
          <h3>Registros de Campañas</h3>
        </div>
        <div className="card-content">
          <LoadingSpinner message="Cargando datos..." />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="card">
        <div className="card-header">
          <h3>Registros de Campañas</h3>
        </div>
        <div className="card-content">
          <ErrorMessage message={error} onRetry={onRefresh} />
        </div>
      </div>
    );
  }

  return (
    <div className="card">
      <div className="card-header">
        <h3>Registros de Campañas</h3>
        <div className="card-actions">
          <button
            className="btn btn-secondary"
            onClick={onRefresh}
            title="Refrescar datos"
            ref={refreshBtnRef}
            type="button"
          >
            <i className="fas fa-sync-alt"></i>
          </button>
        </div>
      </div>

      <div className="card-filters enhanced-layout">
        <div className="filter-section">
          <div className="filter-header">
            <i className="fas fa-filter"></i>
            <span>Filtrar por Campaña</span>
          </div>
          <div className="filter-controls">
            <div className="select-wrapper">
              <select
                value={filtrocamp}
                onChange={(e) => setFiltrocamp(e.target.value)}
                className="campaign-select"
              >
                <option value="">Todas las campañas</option>
                {uniqueCampaigns.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
              <i className="fas fa-chevron-down select-arrow"></i>
            </div>
            {filtrocamp && (
              <button
                className="btn btn-clear-filter"
                onClick={() => setFiltrocamp("")}
                title="Limpiar filtro"
                type="button"
              >
                <i className="fas fa-times"></i>
              </button>
            )}
          </div>
        </div>

        <div className="filter-section danger-section right-aligned">
          <div className="filter-header">
            <i className="fas fa-trash"></i>
            <span>Eliminar Campaña</span>
          </div>
          <div className="filter-controls">
            <div className="select-wrapper">
              <select
                value={selectedCampaign}
                onChange={(e) => setSelectedCampaign(e.target.value)}
                className="campaign-select danger"
              >
                <option value="">Selecciona campaña...</option>
                {uniqueCampaigns.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
              <i className="fas fa-chevron-down select-arrow"></i>
            </div>
            <button
              className="btn btn-danger"
              onClick={askDeleteFromTable}
              disabled={!selectedCampaign || deleting}
              title="Eliminar todos los registros de la campaña seleccionada"
              type="button"
            >
              {deleting ? (
                <>
                  <i className="fas fa-spinner fa-spin"></i> Eliminando...
                </>
              ) : (
                <>
                  <i className="fas fa-trash"></i> Eliminar
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {deleteMsg && (
        <div
          className={`alert ${
            deleteMsg.startsWith("✅")
              ? "alert-success"
              : deleteMsg.startsWith("❌")
              ? "alert-error"
              : "alert-info"
          }`}
          style={{ margin: "0 16px" }}
        >
          {deleteMsg}
        </div>
      )}

      <div className="card-content">
        <RegistrosTableSection data={campaignScopedData} filtrocamp={filtrocamp} />
        <AfpTableSection data={campaignScopedData} filtrocamp={filtrocamp} />
      </div>

      <ConfirmDialog
        open={confirmOpen}
        title="Eliminar campaña"
        message={`¿Eliminar TODOS los registros de la campaña "${selectedCampaign}"? Esta acción no se puede deshacer.`}
        confirmText="Eliminar"
        cancelText="Cancelar"
        onCancel={() => setConfirmOpen(false)}
        onConfirm={doDeleteFromTable}
      />
    </div>
  );
}

// ========================= SECCIÓN TABLA: REGISTROS =========================
function RegistrosTableSection({ data, filtrocamp }) {
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });

  useEffect(() => {
    setCurrentPage(1);
  }, [filtrocamp, searchTerm]);

  const requestSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") direction = "desc";
    setSortConfig({ key, direction });
  };

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return <i className="fas fa-sort"></i>;
    return sortConfig.direction === "asc" ? (
      <i className="fas fa-sort-up"></i>
    ) : (
      <i className="fas fa-sort-down"></i>
    );
  };

  const sortedData = React.useMemo(() => {
    const src = Array.isArray(data) ? [...data] : [];
    if (sortConfig.key) {
      src.sort((a, b) => {
        const av = a?.[sortConfig.key] ?? "";
        const bv = b?.[sortConfig.key] ?? "";
        if (av < bv) return sortConfig.direction === "asc" ? -1 : 1;
        if (av > bv) return sortConfig.direction === "asc" ? 1 : -1;
        return 0;
      });
    }
    return src;
  }, [data, sortConfig]);

  const filteredData = React.useMemo(() => {
    const term = searchTerm.toLowerCase();
    return (sortedData || []).filter((item) => {
      const display = getCampaignDisplay(item);
      return (
        String(item.phoneNumber || "").toLowerCase().includes(term) ||
        String(item.cedula || "").toLowerCase().includes(term) ||
        String(item.tiempo_entre_llamadas ?? "").toLowerCase().includes(term) ||
        String(item.nombres || "").toLowerCase().includes(term) ||
        String(item.flujo_campania || "").toLowerCase().includes(term) ||
        display.toLowerCase().includes(term) ||
        String(item.selectedQueue || "").toLowerCase().includes(term) ||
        String(item.estado || "").toLowerCase().includes(term) ||
        String(item.fecha_reagenda ?? "").toLowerCase().includes(term) ||
        String(item.cita_afp ?? "").toLowerCase().includes(term)
      );
    });
  }, [sortedData, searchTerm]);

  const totalPages = Math.ceil(filteredData.length / itemsPerPage) || 1;
  const currentItems = filteredData.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  return (
    <div style={{ marginBottom: 18 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
        <h3 style={{ margin: "0 0 10px 0" }}>Registros</h3>

        <div className="search-box" style={{ marginBottom: 10 }}>
          <i className="fas fa-search"></i>
          <input
            type="text"
            placeholder="Buscar en Registros..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      <div className="table-responsive">
        <table>
          <thead>
            <tr>
              <th onClick={() => requestSort("nombres")}>Nombre {getSortIcon("nombres")}</th>
              <th onClick={() => requestSort("phoneNumber")}>Teléfono {getSortIcon("phoneNumber")}</th>
              <th onClick={() => requestSort("cedula")}>Cédula {getSortIcon("cedula")}</th>
              <th onClick={() => requestSort("estado")}>Estado {getSortIcon("estado")}</th>
              <th onClick={() => requestSort("nombre_campania")}>Campaña {getSortIcon("nombre_campania")}</th>
              <th onClick={() => requestSort("flujo_campania")}>Flujo {getSortIcon("flujo_campania")}</th>
              <th onClick={() => requestSort("intentos")}>Intentos {getSortIcon("intentos")}</th>
              <th onClick={() => requestSort("intentos_actuales")}>Realizados {getSortIcon("intentos_actuales")}</th>
              <th onClick={() => requestSort("tiempo_entre_llamadas")}>Intervalo {getSortIcon("tiempo_entre_llamadas")}</th>
              <th onClick={() => requestSort("ultimo_intento")}>Último intento {getSortIcon("ultimo_intento")}</th>
              <th onClick={() => requestSort("fecha_reagenda")}>Fecha agendada {getSortIcon("fecha_reagenda")}</th>
              <th onClick={() => requestSort("cita_afp")}>Cita AFP {getSortIcon("cita_afp")}</th>
            </tr>
          </thead>
          <tbody>
            {currentItems.length > 0 ? (
              currentItems.map((item, index) => (
                <TableRow
                  key={`${String(item?.campaign_id || item?.campaignId || "noid")}-${String(item?.phoneNumber || index)}`}
                  item={item}
                />
              ))
            ) : (
              <tr>
                <td colSpan="12" className="no-results">
                  <i className="fas fa-database"></i>
                  No se encontraron registros
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {filteredData.length > 0 && (
        <TableFooter
          currentPage={currentPage}
          totalPages={totalPages}
          itemsPerPage={itemsPerPage}
          filteredData={filteredData}
          setCurrentPage={setCurrentPage}
          filtrocamp={filtrocamp}
        />
      )}
    </div>
  );
}

// ========================= SECCIÓN TABLA: CITA AFP =========================
function AfpTableSection({ data, filtrocamp }) {
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPageAfp, setCurrentPageAfp] = useState(1);
  const [itemsPerPage] = useState(10);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });

  useEffect(() => {
    setCurrentPageAfp(1);
  }, [filtrocamp, searchTerm]);

  const requestSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") direction = "desc";
    setSortConfig({ key, direction });
  };

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return <i className="fas fa-sort"></i>;
    return sortConfig.direction === "asc" ? (
      <i className="fas fa-sort-up"></i>
    ) : (
      <i className="fas fa-sort-down"></i>
    );
  };

  const afpBaseData = React.useMemo(() => {
    const src = Array.isArray(data) ? data : [];
    return src.filter((item) => {
      const citaAfpDt =
        DataUtils.tryParseDate(item?.cita_afp) || DataUtils.extractAfpAppointmentDate(item);
      return !!citaAfpDt;
    });
  }, [data]);

  const sortedAfp = React.useMemo(() => {
    const src = [...afpBaseData];
    if (sortConfig.key) {
      src.sort((a, b) => {
        const av = a?.[sortConfig.key] ?? "";
        const bv = b?.[sortConfig.key] ?? "";

        if (sortConfig.key === "cita_afp") {
          const da = DataUtils.tryParseDate(av) || DataUtils.extractAfpAppointmentDate(a);
          const db = DataUtils.tryParseDate(bv) || DataUtils.extractAfpAppointmentDate(b);
          const ta = da ? da.getTime() : 0;
          const tb = db ? db.getTime() : 0;
          if (ta < tb) return sortConfig.direction === "asc" ? -1 : 1;
          if (ta > tb) return sortConfig.direction === "asc" ? 1 : -1;
          return 0;
        }

        if (av < bv) return sortConfig.direction === "asc" ? -1 : 1;
        if (av > bv) return sortConfig.direction === "asc" ? 1 : -1;
        return 0;
      });
    }
    return src;
  }, [afpBaseData, sortConfig]);

  const filteredAfp = React.useMemo(() => {
    const term = searchTerm.toLowerCase();
    return (sortedAfp || []).filter((item) => {
      const display = getCampaignDisplay(item);
      return (
        String(item.nombres || "").toLowerCase().includes(term) ||
        String(item.phoneNumber || "").toLowerCase().includes(term) ||
        String(item.cedula || "").toLowerCase().includes(term) ||
        String(item.estado || "").toLowerCase().includes(term) ||
        display.toLowerCase().includes(term) ||
        String(item.ultimo_intento || "").toLowerCase().includes(term) ||
        String(item.fecha_reagenda || "").toLowerCase().includes(term) ||
        String(item.cita_afp || "").toLowerCase().includes(term)
      );
    });
  }, [sortedAfp, searchTerm]);

  const totalPagesAfp = Math.ceil(filteredAfp.length / itemsPerPage) || 1;
  const currentItemsAfp = filteredAfp.slice(
    (currentPageAfp - 1) * itemsPerPage,
    currentPageAfp * itemsPerPage
  );

  return (
    <div style={{ marginBottom: 18 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
        <h3 style={{ margin: "0 0 10px 0" }}>Cita AFP</h3>

        <div className="search-box" style={{ marginBottom: 10 }}>
          <i className="fas fa-search"></i>
          <input
            type="text"
            placeholder="Buscar en Cita AFP..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      <div className="table-responsive">
        <table>
          <thead>
            <tr>
              <th onClick={() => requestSort("nombres")}>Nombre {getSortIcon("nombres")}</th>
              <th onClick={() => requestSort("phoneNumber")}>Teléfono {getSortIcon("phoneNumber")}</th>
              <th onClick={() => requestSort("cedula")}>Cédula {getSortIcon("cedula")}</th>
              <th onClick={() => requestSort("estado")}>Estado {getSortIcon("estado")}</th>
              <th onClick={() => requestSort("nombre_campania")}>Campaña {getSortIcon("nombre_campania")}</th>
              <th onClick={() => requestSort("cita_afp")}>Cita AFP {getSortIcon("cita_afp")}</th>
            </tr>
          </thead>
          <tbody>
            {currentItemsAfp.length > 0 ? (
              currentItemsAfp.map((item, index) => (
                <TableRowAfp
                  key={`afp-${String(item?.campaign_id || item?.campaignId || "noid")}-${String(item?.phoneNumber || index)}`}
                  item={item}
                />
              ))
            ) : (
              <tr>
                <td colSpan="8" className="no-results">
                  <i className="fas fa-calendar-times"></i>
                  No hay registros con Cita AFP
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {filteredAfp.length > 0 && (
        <TableFooterAfp
          currentPage={currentPageAfp}
          totalPages={totalPagesAfp}
          itemsPerPage={itemsPerPage}
          totalItems={filteredAfp.length}
          setCurrentPage={setCurrentPageAfp}
          filtrocamp={filtrocamp}
        />
      )}
    </div>
  );
}

const TableRow = ({ item }) => {
  const display = getCampaignDisplay(item);
  const citaAfpDt =
    DataUtils.tryParseDate(item?.cita_afp) || DataUtils.extractAfpAppointmentDate(item);

  return (
    <tr>
      <td>{item.nombres || "-"}</td>
      <td>{item.phoneNumber || "-"}</td>
      <td>{item.cedula || "-"}</td>
      <td>
        <span className={`status-badge ${String(item.estado || "").toLowerCase()}`}>
          {item.estado || "-"}
        </span>
      </td>
      <td>{display}</td>
      <td>{item.flujo_campania || "-"}</td>
      <td>{item.intentos ?? "-"}</td>
      <td>{item.intentos_actuales ?? "0"}</td>
      <td>
        {item.tiempo_entre_llamadas
          ? `${parseInt(item.tiempo_entre_llamadas, 10)} min`
          : "-"}
      </td>
      <td>
        {item.ultimo_intento
          ? DataUtils.formatDateTime12h(DataUtils.tryParseDate(item.ultimo_intento))
          : "-"}
      </td>
      <td>
        {item.fecha_reagenda
          ? DataUtils.formatDateTime12h(DataUtils.tryParseDate(item.fecha_reagenda))
          : "-"}
      </td>
      <td>{citaAfpDt ? DataUtils.formatDateTime12h(citaAfpDt) : "-"}</td>
    </tr>
  );
};

const TableRowAfp = ({ item }) => {
  const display = getCampaignDisplay(item);
  const citaAfpDt =
    DataUtils.tryParseDate(item?.cita_afp) || DataUtils.extractAfpAppointmentDate(item);

  return (
    <tr>
      <td>{item.nombres || "-"}</td>
      <td>{item.phoneNumber || "-"}</td>
      <td>{item.cedula || "-"}</td>
      <td>
        <span className={`status-badge ${String(item.estado || "").toLowerCase()}`}>
          {item.estado || "-"}
        </span>
      </td>
      <td>{display}</td>
      <td>{citaAfpDt ? DataUtils.formatDateTime12h(citaAfpDt) : "-"}</td>
    </tr>
  );
};

const TableFooter = ({
  currentPage,
  totalPages,
  itemsPerPage,
  filteredData,
  setCurrentPage,
  filtrocamp,
}) => (
  <div className="table-footer">
    <div className="table-info">
      Mostrando {(currentPage - 1) * itemsPerPage + 1} a{" "}
      {Math.min(currentPage * itemsPerPage, filteredData.length)} de{" "}
      {filteredData.length} registros
      {filtrocamp && (
        <span className="filter-indicator">
          <i className="fas fa-filter"></i> Filtrado por: {filtrocamp}
        </span>
      )}
    </div>
    <div className="pagination-controls">
      <button
        onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
        disabled={currentPage === 1}
        type="button"
      >
        <i className="fas fa-chevron-left"></i>
      </button>
      <span>
        {" "}
        Página {currentPage} de {totalPages}{" "}
      </span>
      <button
        onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
        disabled={currentPage === totalPages}
        type="button"
      >
        <i className="fas fa-chevron-right"></i>
      </button>
    </div>
  </div>
);

const TableFooterAfp = ({
  currentPage,
  totalPages,
  itemsPerPage,
  totalItems,
  setCurrentPage,
  filtrocamp,
}) => (
  <div className="table-footer">
    <div className="table-info">
      Mostrando {(currentPage - 1) * itemsPerPage + 1} a{" "}
      {Math.min(currentPage * itemsPerPage, totalItems)} de {totalItems} citas AFP
      {filtrocamp && (
        <span className="filter-indicator">
          <i className="fas fa-filter"></i> Filtrado por: {filtrocamp}
        </span>
      )}
    </div>
    <div className="pagination-controls">
      <button
        onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
        disabled={currentPage === 1}
        type="button"
      >
        <i className="fas fa-chevron-left"></i>
      </button>
      <span>
        {" "}
        Página {currentPage} de {totalPages}{" "}
      </span>
      <button
        onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
        disabled={currentPage === totalPages}
        type="button"
      >
        <i className="fas fa-chevron-right"></i>
      </button>
    </div>
  </div>
);

// ========================= CONFIGURAR CAMPAÑA (PÁGINAS) =========================
function ConfigPage({ goToCampania }) {
  const [nombreCampania, setNombreCampania] = useState("");
  const [tiempoLlamada, setTiempoLlamada] = useState(360);
  const [intentos, setIntentos] = useState(3);
  const [tipoLlamada, setTipoLlamada] = useState("Bot");
  const [selectedFile, setSelectedFile] = useState(null);
  const [flujo, setFlujo] = useState("");
  const [previewRows, setPreviewRows] = useState([]);
  const [previewHeaders, setPreviewHeaders] = useState([]);
  const [flows, setFlowsList] = useState([]);
  const [queues, setQueues] = useState([]);
  const [selectedQueue, setSelectedQueue] = useState("");
  const [loadingFlows, setLoadingFlows] = useState(true);
  const [loadingQueues, setLoadingQueues] = useState(true);
  const [fileError, setFileError] = useState("");
  const [step, setStep] = useState(1);

  useEffect(() => {
    setLoadingFlows(true);
    ApiService.fetchWithRetry(API_CONFIG.BASE + "?flows=1")
      .then((data) => {
        if (Array.isArray(data?.flows) && data.flows.length > 0) {
          setFlowsList(data.flows);
          setFlujo("");
        } else {
          setFlowsList([]);
          setFlujo("");
        }
      })
      .finally(() => setLoadingFlows(false));

    setLoadingQueues(true);
    ApiService.fetchWithRetry(API_CONFIG.BASE + "?queues=1")
      .then((data) => setQueues(data?.queues || []))
      .finally(() => setLoadingQueues(false));
  }, []);

  const handleFile = (file) => {
    setSelectedFile(file);
    const ext = file.name.split(".").pop().toLowerCase();

    if (ext === "csv") {
      Papa.parse(file, {
        header: true,
        skipEmptyLines: true,
        complete: (results) => {
          setPreviewHeaders(results.meta.fields || []);
          setPreviewRows(results.data || []);
        },
      });
    } else if (ext === "xlsx") {
      const reader = new FileReader();
      reader.onload = (e) => {
        const workbook = XLSX.read(e.target.result, { type: "binary" });
        const sheetName = workbook.SheetNames[0];
        const data = XLSX.utils.sheet_to_json(workbook.Sheets[sheetName], { defval: "" });
        setPreviewHeaders(Object.keys(data[0] || {}));
        setPreviewRows(data);
      };
      reader.readAsBinaryString(file);
    } else {
      setPreviewRows([]);
      setPreviewHeaders([]);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!selectedFile) {
      setFileError("⚠️ Debes subir un archivo antes de continuar.");
      return;
    }

    if (!Number.isFinite(+tiempoLlamada) || +tiempoLlamada <= 0) {
      setFileError("⚠️ El tiempo entre intentos debe ser un número mayor a 0.");
      return;
    }

    setFileError("");

    const campaignId = makeCampaignId(nombreCampania);

    goToCampania({
      campaignId,
      nombreCampania,
      tiempoLlamada: Number(tiempoLlamada),
      intentos: Number(intentos),
      tipoLlamada,
      flujo,
      archivo: selectedFile,
      previewHeaders,
      previewRows,
      selectedQueue,
      tipoflujo: String(tipoLlamada || "Bot").toLowerCase(),
    });
  };

  const nextStep = () => {
    if (step === 1 && (!nombreCampania || !selectedFile)) {
      setFileError("Debes ingresar un nombre y subir un archivo");
      return;
    }
    setStep(step + 1);
  };

  const prevStep = () => setStep(step - 1);

  return (
    <div className="config-container">
      <div className="config-header">
        <h1>Configurar Nueva Campaña</h1>
        <div className="steps-indicator">
          <div className={`step ${step >= 1 ? "active" : ""}`}>
            <span>1</span> Información Básica
          </div>
          <div className={`step ${step >= 2 ? "active" : ""}`}>
            <span>2</span> Configuración
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        {step === 1 && (
          <ConfigStep1
            nombreCampania={nombreCampania}
            setNombreCampania={setNombreCampania}
            selectedFile={selectedFile}
            handleFile={handleFile}
            previewRows={previewRows}
            previewHeaders={previewHeaders}
            fileError={fileError}
            onNext={nextStep}
          />
        )}

        {step === 2 && (
          <ConfigStep2
            flujo={flujo}
            setFlujo={setFlujo}
            tiempoLlamada={tiempoLlamada}
            setTiempoLlamada={setTiempoLlamada}
            intentos={intentos}
            setIntentos={setIntentos}
            flows={flows}
            loadingFlows={loadingFlows}
            tipoLlamada={tipoLlamada}
            setTipoLlamada={setTipoLlamada}
            queues={queues}
            selectedQueue={selectedQueue}
            setSelectedQueue={setSelectedQueue}
            loadingQueues={loadingQueues}
            onBack={prevStep}
            onSubmit={handleSubmit}
          />
        )}
      </form>
    </div>
  );
}

const ConfigStep1 = ({
  nombreCampania,
  setNombreCampania,
  selectedFile,
  handleFile,
  previewRows,
  previewHeaders,
  fileError,
  onNext,
}) => (
  <div className="config-step">
    <div className="form-group">
      <label>Nombre de la Campaña</label>
      <input
        value={nombreCampania}
        onChange={(e) => setNombreCampania(e.target.value)}
        placeholder="Ej: Campaña de Recordatorios"
        required
        type="text"
      />
    </div>

    <div className="form-group">
      <label>Subir Base de Contactos</label>
      <FileUpload selectedFile={selectedFile} handleFile={handleFile} previewCount={previewRows.length} />
      {fileError && (
        <div className="form-error">
          <i className="fas fa-exclamation-circle"></i> {fileError}
        </div>
      )}
    </div>

    {previewRows.length > 0 && (
      <PreviewTable previewRows={previewRows} previewHeaders={previewHeaders} />
    )}

    <div className="form-actions">
      <button
        type="button"
        className="btn btn-primary"
        onClick={onNext}
        disabled={!nombreCampania || !selectedFile}
      >
        Siguiente <i className="fas fa-arrow-right"></i>
      </button>
    </div>
  </div>
);

const FileUpload = ({ selectedFile, handleFile, previewCount = 0 }) => (
  <div className="file-upload-container">
    <label className="file-upload-label">
      <div className="upload-icon">
        <i className="fas fa-cloud-upload-alt"></i>
      </div>
      <div className="upload-text">
        {selectedFile ? (
          <>
            <strong>{selectedFile.name}</strong>
            <span>
              {Math.round(selectedFile.size / 1024)} KB
              {previewCount ? ` — ${previewCount} registros` : ""}
            </span>
          </>
        ) : (
          <>
            <strong>Arrastra tu archivo aquí o haz clic</strong>
            <span>Formatos soportados: .xlsx, .csv</span>
          </>
        )}
      </div>
      <input
        type="file"
        accept=".csv,.xlsx"
        onChange={(e) => e.target.files?.[0] && handleFile(e.target.files[0])}
      />
    </label>
  </div>
);

const PreviewTable = ({ previewRows, previewHeaders }) => (
  <div className="preview-table-container">
    <h3>Vista Previa de Datos</h3>
    <div className="preview-table">
      <table>
        <thead>
          <tr>
            {previewHeaders.slice(0, 5).map((header, index) => (
              <th key={index}>{header}</th>
            ))}
            {previewHeaders.length > 5 && <th>...</th>}
          </tr>
        </thead>
        <tbody>
          {previewRows.slice(0, 5).map((row, rowIndex) => (
            <tr key={rowIndex}>
              {previewHeaders.slice(0, 5).map((header, colIndex) => (
                <td key={colIndex}>{row?.[header]}</td>
              ))}
              {previewHeaders.length > 5 && <td>...</td>}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
    <div className="preview-info">Mostrando 5 de {previewRows.length} registros</div>
  </div>
);

const ConfigStep2 = ({
  flujo,
  setFlujo,
  tiempoLlamada,
  setTiempoLlamada,
  intentos,
  setIntentos,
  flows,
  loadingFlows,
  tipoLlamada,
  setTipoLlamada,
  queues,
  selectedQueue,
  setSelectedQueue,
  loadingQueues,
  onBack,
  onSubmit,
}) => {
  const getFlowName = (f, idx) => {
    if (!f) return `Flujo ${idx + 1}`;
    if (typeof f === "string") return f;
    return f.name || f.Nombre || f.FlowName || f.id || `Flujo ${idx + 1}`;
  };

  return (
    <div className="config-step">
      <div className="form-grid">
        <div className="form-group">
          <label>Flujo</label>
          {loadingFlows ? (
            <LoadingSpinner message="Cargando flujos..." />
          ) : (
            <select value={flujo} onChange={(e) => setFlujo(e.target.value)}>
              <option value="">Selecciona un flujo...</option>
              {Array.isArray(flows) &&
                flows.map((f, i) => {
                  const name = getFlowName(f, i);
                  return (
                    <option key={name} value={name}>
                      {name}
                    </option>
                  );
                })}
            </select>
          )}
        </div>

        <div className="form-group">
          <label>Tiempo entre intentos (minutos)</label>
          <input
            type="number"
            min="1"
            value={tiempoLlamada}
            onChange={(e) => setTiempoLlamada(e.target.value)}
            placeholder="Ej: 5"
          />
        </div>

        <div className="form-group">
          <label>Número de intentos</label>
          <input
            type="number"
            min="1"
            value={intentos}
            onChange={(e) => setIntentos(e.target.value)}
            placeholder="Ej: 3"
          />
        </div>

        <div className="form-group">
          <label>Tipo de llamada</label>
          <select value={tipoLlamada} onChange={(e) => setTipoLlamada(e.target.value)}>
            <option value="Bot">Bot</option>
            <option value="Agente">Agente</option>
          </select>
        </div>

        {tipoLlamada === "Agente" && (
          <div className="form-group">
            <label>Cola (solo Agente)</label>
            {loadingQueues ? (
              <LoadingSpinner message="Cargando colas..." />
            ) : (
              <select value={selectedQueue} onChange={(e) => setSelectedQueue(e.target.value)}>
                <option value="">Selecciona una cola...</option>
                {Array.isArray(queues) &&
                  queues.map((q, i) => {
                    const name =
                      typeof q === "string"
                        ? q
                        : q.name || q.Nombre || q.QueueName || q.id || `Cola ${i + 1}`;
                    return (
                      <option key={name} value={name}>
                        {name}
                      </option>
                    );
                  })}
              </select>
            )}
          </div>
        )}
      </div>

      <div className="form-actions">
        <button type="button" className="btn btn-outline" onClick={onBack}>
          <i className="fas fa-arrow-left"></i> Atrás
        </button>
        <button type="submit" className="btn btn-primary" onClick={onSubmit}>
          <i className="fas fa-check"></i> Crear Campaña
        </button>
      </div>
    </div>
  );
};

function CampaniaPage({ datos, goToConfig, goToDashboard, refreshDashboardData }) {
  const [mensaje, setMensaje] = useState("");
  const [estadoCampaña, setEstadoCampaña] = useState("activa");
  const [loading, setLoading] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const iniciarCampaniaHandler = async () => {
    setMensaje("Iniciando campaña...");
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("file", datos.archivo);

      if (datos?.campaignId) formData.append("campaign_id", datos.campaignId);

      formData.append("nombre_campania", datos.nombreCampania);
      formData.append("flujo", datos.flujo);
      formData.append("tiempo_entre_llamadas", datos.tiempoLlamada);
      formData.append("intentos", datos.intentos);
      formData.append("selectedQueue", datos.selectedQueue);
      formData.append("tipoflujo", datos.tipoLlamada?.toLowerCase() || "bot");
      formData.append("append", "true");

      const response = await ApiService.fetchWithRetry(API_CONFIG.BASE, {
        method: "POST",
        body: formData,
      });

      if (response?.statusCode === 200) {
        setMensaje("Campaña iniciada con éxito!");
        setEstadoCampaña("activa");
      } else {
        setMensaje("Campaña iniciada con éxito!");
      }

      try {
        await refreshDashboardData?.();
      } catch (e) {
        console.warn("No se pudo refrescar el dashboard:", e);
      }

      goToDashboard?.();
    } catch (error) {
      console.error(error);
      setMensaje("Error al iniciar la campaña");
    } finally {
      setLoading(false);
    }
  };

  const askEliminarCampania = () => setConfirmOpen(true);

  const doEliminarCampania = async () => {
    setConfirmOpen(false);
    if (!datos?.nombreCampania) {
      setMensaje("No hay nombre de campaña para eliminar");
      return;
    }
    setLoading(true);
    setMensaje("Eliminando campaña...");
    try {
      const r = await ApiService.deleteCampaignByName(datos.nombreCampania, { dryRun: false });
      if (typeof r?.deleted === "number") {
        setMensaje(`✅ Eliminados ${r.deleted} registros (encontrados: ${r.found}).`);
        setEstadoCampaña("pausada");
      } else {
        setMensaje("No se pudo eliminar la campaña (respuesta inesperada).");
      }
    } catch (e) {
      console.error(e);
      setMensaje("❌ Error al eliminar la campaña.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="campaign-container">
      <div className="campaign-header">
        <button type="button" className="btn btn-outline" onClick={goToConfig}>
          <i className="fas fa-arrow-left"></i> Volver
        </button>
        <h1>
          {datos?.nombreCampania}{" "}
          {datos?.campaignId ? (
            <span className="badge-upload">ID: {datos.campaignId.slice(0, 8)}</span>
          ) : null}
        </h1>
        <div className="campaign-status-container"></div>
      </div>

      {mensaje && (
        <div
          className={`alert ${
            mensaje.includes("éxito") || mensaje.startsWith("✅")
              ? "alert-success"
              : "alert-error"
          }`}
        >
          <i
            className={`fas ${
              mensaje.includes("éxito") || mensaje.startsWith("✅")
                ? "fa-check-circle"
                : "fa-exclamation-circle"
            }`}
          ></i>
          {mensaje}
        </div>
      )}

      <div className="campaign-grid">
        <CampaignInfoCard datos={datos} />
        <CampaignConfigCard datos={datos} />
      </div>

      <DataPreview datos={datos} />

      <div className="campaign-actions">
        <button
          type="button"
          className="btn btn-primary"
          onClick={iniciarCampaniaHandler}
          disabled={loading}
        >
          {loading ? (
            <>
              <i className="fas fa-spinner fa-spin"></i> Procesando...
            </>
          ) : (
            <>
              <i className="fas fa-play"></i> Iniciar Campaña
            </>
          )}
        </button>

        <button
          type="button"
          className="btn btn-delete-campaign"
          onClick={askEliminarCampania}
          disabled={loading}
        >
          {loading ? (
            <>
              <i className="fas fa-spinner fa-spin"></i> Eliminando...
            </>
          ) : (
            <>
              <i className="fas fa-trash"></i> Eliminar Campaña
            </>
          )}
        </button>
      </div>

      <ConfirmDialog
        open={confirmOpen}
        title="Eliminar campaña"
        message={`¿Eliminar TODOS los contactos de la campaña "${datos?.nombreCampania}"? Esta acción no se puede deshacer.`}
        confirmText="Eliminar"
        cancelText="Cancelar"
        onCancel={() => setConfirmOpen(false)}
        onConfirm={doEliminarCampania}
      />
    </div>
  );
}

const CampaignInfoCard = ({ datos }) => (
  <div className="campaign-card">
    <div className="card-header">
      <h3>Información de la Campaña</h3>
      <i className="fas fa-info-circle"></i>
    </div>
    <div className="card-content">
      <div className="info-item">
        <span className="info-label">Flujo Asociado:</span>
        <span className="info-value">{datos?.flujo || "-"}</span>
      </div>
      <div className="info-item">
        <span className="info-label">Contactos:</span>
        <span className="info-value">{datos?.previewRows?.length ?? 0}</span>
      </div>
      {datos?.campaignId && (
        <div className="info-item">
          <span className="info-label">Campaign ID:</span>
          <span className="info-value">{datos.campaignId}</span>
        </div>
      )}
    </div>
  </div>
);

const CampaignConfigCard = ({ datos }) => (
  <div className="campaign-card">
    <div className="card-header">
      <h3>Configuración</h3>
      <i className="fas fa-cog"></i>
    </div>
    <div className="card-content">
      <div className="info-item">
        <span className="info-label">Tiempo entre intentos:</span>
        <span className="info-value">{datos?.tiempoLlamada} minutos</span>
      </div>
      <div className="info-item">
        <span className="info-label">Número de intentos:</span>
        <span className="info-value">{datos?.intentos}</span>
      </div>
      {datos?.tipoLlamada === "Agente" && (
        <div className="info-item">
          <span className="info-label">Cola asignada:</span>
          <span className="info-value">{datos?.selectedQueue || "-"}</span>
        </div>
      )}
    </div>
  </div>
);

const DataPreview = ({ datos }) => (
  <div className="data-preview">
    <h3>Vista Previa de Contactos</h3>
    <div className="preview-table-container">
      <table>
        <thead>
          <tr>
            {datos?.previewHeaders?.slice(0, 6).map((header, index) => (
              <th key={index}>{header}</th>
            ))}
            {datos?.previewHeaders?.length > 6 && <th>...</th>}
          </tr>
        </thead>
        <tbody>
          {datos?.previewRows?.slice(0, 5).map((row, rowIndex) => (
            <tr key={rowIndex}>
              {datos?.previewHeaders?.slice(0, 6).map((header, colIndex) => (
                <td key={colIndex}>{row?.[header]}</td>
              ))}
              {datos?.previewHeaders?.length > 6 && <td>...</td>}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

// ========================= UI REUTILIZABLES =========================
function LoadingSpinner({ message = "Cargando..." }) {
  return (
    <div className="loading-container" role="status" aria-live="polite">
      <div className="loading-spinner"></div>
      <span>{message}</span>
    </div>
  );
}

function ErrorMessage({ message = "Ocurrió un error", onRetry }) {
  return (
    <div className="error-container" role="alert">
      <i className="fas fa-exclamation-triangle" aria-hidden="true"></i>
      <span>{message}</span>
      {onRetry && (
        <button onClick={onRetry} className="btn-retry" type="button">
          Reintentar
        </button>
      )}
    </div>
  );
}

function ConfirmDialog({
  open,
  title = "Confirmación",
  message,
  confirmText = "Confirmar",
  cancelText = "Cancelar",
  onConfirm,
  onCancel,
}) {
  if (!open) return null;

  return (
    <div
      className="modal-backdrop"
      role="dialog"
      aria-modal="true"
      aria-labelledby="confirm-title"
    >
      <div className="modal-card">
        <div className="modal-header">
          <div className="modal-icon">
            <i className="fas fa-exclamation-triangle" aria-hidden="true"></i>
          </div>
          <h3 id="confirm-title">{title}</h3>
        </div>

        <div className="modal-body">
          <p>{message}</p>
        </div>

        <div className="modal-actions">
          <button className="btn btn-outline" onClick={onCancel} type="button">
            {cancelText}
          </button>
          <button className="btn btn-danger" onClick={onConfirm} type="button">
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  );
}
// ========================= FIN DEL ARCHIVO =========================
